import { useEffect, useState } from 'react';
import { CheckCircle, XCircle, AlertCircle, Loader2, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICES = [
  { id: 'api', name: 'PipeGuide API', description: 'Core backend' },
  { id: 'recall', name: 'Recall.ai', description: 'Meeting bot provider' },
  { id: 'openai', name: 'OpenAI', description: 'AI scoring & analysis' },
  { id: 'paystack', name: 'Paystack', description: 'Payments' },
  { id: 'mongo', name: 'Database', description: 'MongoDB Atlas' },
];

const statusStyles = {
  ok: { label: 'Operational', icon: CheckCircle, color: '#10B981', bg: '#ECFDF5' },
  degraded: { label: 'Degraded', icon: AlertCircle, color: '#F59E0B', bg: '#FFFBEB' },
  down: { label: 'Outage', icon: XCircle, color: '#EF4444', bg: '#FEF2F2' },
  unknown: { label: 'Checking…', icon: Loader2, color: '#A1A1AA', bg: '#F4F4F5' },
};

export default function StatusPage() {
  const [statuses, setStatuses] = useState({});
  const [loading, setLoading] = useState(true);
  const [lastChecked, setLastChecked] = useState(null);

  const fetchStatus = async () => {
    try {
      const { data } = await axios.get(`${API}/status`);
      setStatuses(data.services || {});
      setLastChecked(new Date());
    } catch (e) {
      // API itself is down — show that explicitly
      setStatuses({ api: { status: 'down', message: 'PipeGuide API unreachable' } });
      setLastChecked(new Date());
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, 60000); // refresh every 60s
    return () => clearInterval(interval);
  }, []);

  const allOk = SERVICES.every(s => (statuses[s.id]?.status || 'ok') === 'ok');
  const anyDown = SERVICES.some(s => statuses[s.id]?.status === 'down');

  const overall = anyDown ? 'down' : (allOk ? 'ok' : 'degraded');
  const overallStyle = statusStyles[overall];

  return (
    <div className="min-h-screen bg-zinc-50">
      <header className="bg-brand-primary text-white px-6 py-4">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <Link to="/" className="text-sm opacity-80 hover:opacity-100 flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" />
            Back to app
          </Link>
          <div className="text-sm font-bold tracking-wide">PipeGuide Status</div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10">
        {/* Overall status banner */}
        <div
          className="rounded-xl border p-6 mb-6 flex items-center gap-4"
          style={{ background: overallStyle.bg, borderColor: overallStyle.color + '40' }}
          data-testid="status-overall-banner"
        >
          <overallStyle.icon
            className="w-10 h-10 flex-shrink-0"
            style={{ color: overallStyle.color }}
          />
          <div>
            <div className="text-lg font-bold" style={{ color: overallStyle.color }}>
              {overall === 'ok' ? 'All systems operational' : overall === 'degraded' ? 'Partial system disruption' : 'Major outage in progress'}
            </div>
            <div className="text-sm text-zinc-700 mt-0.5">
              {lastChecked ? `Last checked ${lastChecked.toLocaleTimeString()}` : 'Checking…'}
              {' · auto-refreshes every minute'}
            </div>
          </div>
        </div>

        {/* Per-service list */}
        <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden" data-testid="status-service-list">
          {SERVICES.map((svc, i) => {
            const st = statuses[svc.id]?.status || (loading ? 'unknown' : 'ok');
            const style = statusStyles[st] || statusStyles.unknown;
            const Icon = style.icon;
            return (
              <div
                key={svc.id}
                className={`px-5 py-4 flex items-center justify-between ${i > 0 ? 'border-t border-zinc-100' : ''}`}
                data-testid={`status-service-${svc.id}`}
              >
                <div>
                  <div className="font-semibold text-sm text-zinc-900">{svc.name}</div>
                  <div className="text-xs text-zinc-500 mt-0.5">{svc.description}</div>
                </div>
                <div className="flex items-center gap-2 text-sm font-medium" style={{ color: style.color }}>
                  <Icon className={`w-4 h-4 ${st === 'unknown' ? 'animate-spin' : ''}`} />
                  {style.label}
                </div>
              </div>
            );
          })}
        </div>

        {/* Help footer */}
        <div className="mt-8 text-center text-sm text-zinc-500">
          Something looks broken on your side but green here?{' '}
          <a href="mailto:support@pipeguide.tech" className="text-brand-primary font-semibold">
            Email support
          </a>
        </div>
      </main>
    </div>
  );
}
